import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import { isTour2027LaunchCampaignActive, TOUR_2027_LAUNCH_CAMPAIGN_CONFIG } from 'constants/tours'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'

interface CountdownTimeParts {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const LAST_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000

function getCountdownTimeParts(endDate: string): CountdownTimeParts | null {
  const endTime = new Date(endDate).getTime()

  if (Number.isNaN(endTime)) {
    return null
  }

  const timeDifference = endTime - Date.now()

  if (timeDifference <= 0) {
    return null
  }

  const totalSeconds = Math.floor(timeDifference / 1000)

  return {
    days: Math.floor(totalSeconds / (24 * 60 * 60)),
    hours: Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60)),
    minutes: Math.floor((totalSeconds % (60 * 60)) / 60),
    seconds: totalSeconds % 60,
  }
}

function formatCountdownValue(value: number): string {
  return String(value).padStart(2, '0')
}

function shouldShowCountdownTimer(endDate: string): boolean {
  const endTime = new Date(endDate).getTime()

  if (Number.isNaN(endTime)) {
    return false
  }

  const timeDifference = endTime - Date.now()

  return timeDifference > 0 && timeDifference <= LAST_DAY_IN_MILLISECONDS
}

const BannerContent = styled.div`
  position: relative;
`

const BannerImage = styled(Image)`
  padding-top: ${rem(5)};
  padding-bottom: ${rem(20)};
  width: 100%;
`

const CountdownCard = styled.div`
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(4)};
  width: fit-content;
  padding: ${rem(6)} ${rem(8)};
  border-radius: 0;
  background-color: #d92d20;
  box-shadow: 0 ${rem(6)} ${rem(14)} rgba(18, 49, 46, 0.22);
`

const CountdownItems = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(3)};
  flex-wrap: wrap;
`

const CountdownItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: ${rem(34)};
`

const CountdownValue = styled.span`
  color: white;
  font-size: ${rem(13)};
  font-weight: 600;
  line-height: 1;
`

const CountdownLabel = styled.span`
  margin-top: ${rem(1)};
  color: rgba(255, 255, 255, 0.8);
  font-size: ${rem(7)};
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1;
  text-transform: uppercase;
`

const CountdownPrefix = styled(BodyText)`
  color: white;
`

function ToursVertical2027TravelBanner() {
  const intl = useIntl()
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const [countdownTimeParts, setCountdownTimeParts] = useState<CountdownTimeParts | null>(() =>
    getCountdownTimeParts(TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.endDate),
  )
  const [shouldShowCountdown, setShouldShowCountdown] = useState<boolean>(() =>
    shouldShowCountdownTimer(TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.endDate),
  )
  const isCampaignActive = isTour2027LaunchCampaignActive(region)

  const altText = intl.formatMessage(
    {
      id: 'g5tnX8H6J_orhU4P8IYY',
      defaultMessage: 'Tours 2027 Campaign',
      description: 'Alt text for the Tours 2027 campaign banner image on the tour page',
    },
  )

  const countdownPrefix = intl.formatMessage({
    id: 'hQjc0_xDL2RUlDOn5Q05',
    defaultMessage: 'Offer ends in',
    description: 'Prefix text displayed above the countdown timer in the Tours 2027 campaign banner on the tours vertical page',
  })

  const daysLabel = intl.formatMessage({
    id: 'LqXdZ0eyhT7dSLqJEBsJ',
    defaultMessage: 'Days',
    description: 'Unit label for days shown in the countdown timer on the Tours 2027 campaign banner on the tours vertical page',
  })

  const hoursLabel = intl.formatMessage({
    id: 'zhwwaINNRdiGKjb2jJz4',
    defaultMessage: 'Hours',
    description: 'Unit label for hours shown in the countdown timer on the Tours 2027 campaign banner on the tours vertical page',
  })

  const minutesLabel = intl.formatMessage({
    id: '-VtpHO0efVobmiTAeyFS',
    defaultMessage: 'Minutes',
    description: 'Unit label for minutes shown in the countdown timer on the Tours 2027 campaign banner on the tours vertical page',
  })

  const secondsLabel = intl.formatMessage({
    id: 'mgL7aQrUMBHmRmNCt7jT',
    defaultMessage: 'Seconds',
    description: 'Unit label for seconds shown in the countdown timer on the Tours 2027 campaign banner on the tours vertical page',
  })

  useEffect(() => {
    if (!isCampaignActive) {
      return undefined
    }

    const countdownInterval = window.setInterval(() => {
      setCountdownTimeParts(getCountdownTimeParts(TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.endDate))
      setShouldShowCountdown(shouldShowCountdownTimer(TOUR_2027_LAUNCH_CAMPAIGN_CONFIG.endDate))
    }, 1000)

    return () => {
      window.clearInterval(countdownInterval)
    }
  }, [isCampaignActive])

  if (!isCampaignActive) {
    return null
  }

  return <Clickable to="/search/tours?destinationId=le_d41d8cd98f00b204e9800998ecf8427e&destinationName=Anywhere&campaigns=Tours%202027">
    <CSSBreakpoint>
      <BannerContent>
        <CSSBreakpoint min="desktop">
          <BannerImage image={{ id: 'desktop-tours-2027-banner' }} format="gif" alt={altText} />
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet" max="tablet">
          <BannerImage image={{ id: 'tablet-tours-2027-banner' }} format="gif" alt={altText} />
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <BannerImage image={{ id: 'mobile-tours-2027-banner' }} format="gif" alt={altText} />
        </CSSBreakpoint>
        {shouldShowCountdown && countdownTimeParts && <CountdownCard aria-label={countdownPrefix}>
          <CountdownPrefix variant="small">{countdownPrefix}</CountdownPrefix>
          <CountdownItems>
            <CountdownItem>
              <CountdownValue>{formatCountdownValue(countdownTimeParts.days)}</CountdownValue>
              <CountdownLabel>{daysLabel}</CountdownLabel>
            </CountdownItem>
            <CountdownItem>
              <CountdownValue>{formatCountdownValue(countdownTimeParts.hours)}</CountdownValue>
              <CountdownLabel>{hoursLabel}</CountdownLabel>
            </CountdownItem>
            <CountdownItem>
              <CountdownValue>{formatCountdownValue(countdownTimeParts.minutes)}</CountdownValue>
              <CountdownLabel>{minutesLabel}</CountdownLabel>
            </CountdownItem>
            <CountdownItem>
              <CountdownValue>{formatCountdownValue(countdownTimeParts.seconds)}</CountdownValue>
              <CountdownLabel>{secondsLabel}</CountdownLabel>
            </CountdownItem>
          </CountdownItems>
        </CountdownCard>}
      </BannerContent>
    </CSSBreakpoint>
  </Clickable>
}

export default ToursVertical2027TravelBanner
